import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Mail, Home, GraduationCap, Globe, Sparkles, Check, Rocket, Target, TrendingUp, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../sections/Navbar';
import Footer from '../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const benefits = [
  'Plat 200 Kč/hodina nástup',
  'Možnost rychlého růstu v oboru',
  'Práce na Home Office',
  'Flexibilní pracovní doba',
  'Práce s nejmodernějšími AI nástroji',
  'Neomezený věk ani národnost',
  'Školení a vzdělávání',
  'Kariérní postup',
];

const requirements = [
  'Zájem o AI a nové technologie',
  'Schopnost pracovat s počítačem',
  'Chuť učit se nové věci',
  'Samostatnost a zodpovědnost',
];

const growthPath = [
  {
    icon: Brain,
    title: 'Začátek',
    description: 'Naučíte se pracovat s AI nástroji a pochopíte základy AI optimalizace.',
  },
  {
    icon: Target,
    title: 'Specialista',
    description: 'Po 3–6 měsících budete samostatně optimalizovat weby pro AI vyhledávače.',
  },
  {
    icon: TrendingUp,
    title: 'Senior / Vedení',
    description: 'Rok a více — můžete vést projekty, školit nové kolegy, rozhodovat o strategii.',
  },
];

const CareerPage = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current?.querySelectorAll('.animate-section') || [], {
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <main className="relative z-10 pt-24 pb-16">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/">
              <Button
                variant="ghost"
                className="mb-6 text-slate-500 hover:text-slate-700 -ml-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět na hlavní stránku
              </Button>
            </Link>

            <div ref={contentRef}>
              {/* Header */}
              <div className="animate-section text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-4">
                  <Sparkles className="w-4 h-4 text-[#0ea5e9]" />
                  <span className="text-sm text-slate-600">Kariéra</span>
                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-slate-800">
                  Přidej se k <span className="text-gradient">nám</span>
                </h1>
                
                <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                  Hledáme nadšence do AI, kteří chtějí růst a tvořit budoucnost 
                  digitálního marketingu.
                </p>
              </div>

              {/* Main Vision */}
              <div className="animate-section glass-card rounded-2xl p-6 md:p-8 mb-8 border-l-4 border-[#0ea5e9]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20 flex items-center justify-center flex-shrink-0">
                    <Rocket className="w-6 h-6 text-[#0ea5e9]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold mb-3 text-slate-800">Chceme z tebe udělat AI specialistu</h2>
                    <p className="text-slate-600 leading-relaxed">
                      Nehledáme někoho, kdo už všechno umí. Hledáme někoho, kdo chce růst. 
                      U nás se naučíš pracovat s nejmodernějšími AI nástroji, optimalizovat weby 
                      pro ChatGPT a Perplexity, a stát se špičkovým odborníkem v oboru, který 
                      se právě rodí. Máš jedinečnou šanci být u toho od začátku.
                    </p>
                  </div>
                </div>
              </div>

              {/* Growth Path */}
              <div className="animate-section mb-12">
                <h2 className="text-2xl font-bold mb-6 text-slate-800 text-center">Jak bude vypadat tvůj růst</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {growthPath.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div key={index} className="glass-card rounded-xl p-5 text-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20 flex items-center justify-center mx-auto mb-3">
                          <Icon className="w-6 h-6 text-[#0ea5e9]" />
                        </div>
                        <h3 className="font-bold text-slate-800 mb-2">{step.title}</h3>
                        <p className="text-sm text-slate-500">{step.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Main Content */}
              <div className="animate-section glass-card rounded-2xl p-6 md:p-8 mb-8">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">
                  Koho hledáme?
                </h2>
                
                <p className="text-slate-600 leading-relaxed mb-6">
                  Hledáme ideálně studenty nebo mladé lidi, kteří chtějí pracovat na našem produktu. 
                  Neomezujeme věk ani národnost — důležitý je zájem o AI a chuť se učit. 
                  Pokud tě baví technologie a chceš být u něčeho nového, jsi tu správně.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Benefits */}
                  <div>
                    <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Co nabízíme
                    </h3>
                    <ul className="space-y-2">
                      {benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9] mt-2 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-[#0ea5e9]" />
                      Co očekáváme
                    </h3>
                    <ul className="space-y-2">
                      {requirements.map((req, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00d4aa] mt-2 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Position Details */}
              <div className="animate-section grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="glass-card rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20 flex items-center justify-center mx-auto mb-3">
                    <Home className="w-6 h-6 text-[#0ea5e9]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1">Home Office</h3>
                  <p className="text-sm text-slate-500">
                    Pracujte odkudkoliv
                  </p>
                </div>
                
                <div className="glass-card rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00d4aa]/20 to-[#14b8a6]/20 flex items-center justify-center mx-auto mb-3">
                    <Globe className="w-6 h-6 text-[#00d4aa]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1">Flexibilita</h3>
                  <p className="text-sm text-slate-500">
                    Volná pracovní doba
                  </p>
                </div>
                
                <div className="glass-card rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#14b8a6]/20 to-[#22c55e]/20 flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-6 h-6 text-[#14b8a6]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1">Růst</h3>
                  <p className="text-sm text-slate-500">
                    Kariérní postup
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="animate-section glass-card rounded-2xl p-6 md:p-8 text-center">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">
                  Máš zájem?
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Pošlete nám svůj životopis nebo krátký popis o sobě. 
                  Ozveme se vám co nejdříve.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="mailto:simonsoucek99@gmail.com?subject=Zájem o práci - AI Optimalizace">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold px-8"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Poslat životopis
                    </Button>
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Kontakt pro zájemce:{' '}
                    <a 
                      href="mailto:simonsoucek99@gmail.com" 
                      className="text-[#0ea5e9] hover:underline font-medium"
                    >
                      simonsoucek99@gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CareerPage;
